/**
 * Hidden Gems discovery page — Converged Extremes preset screener
 * plus the backtested Mid/Small Sleeve.
 */
import React, { useEffect, useState } from "react";
import { Gem } from "lucide-react";
import { useAuth } from "../App";
import { ConvergedScreener } from "../components/stats/ConvergedScreener";
import { MidSmallSleeve } from "../components/stats/MidSmallSleeve";
import { API } from "../components/stats/statsApi";

const VIEWS = [
  { key: "converged", label: "Converged Extremes",
    desc: "Stocks where ≥3 extreme sector specialists (top-5% ia_skew) converged within the last 4 quarters — ranked by specialist-derived target upside, gated by options tape and nowcast regime. Big-cap dominated by nature (mid/smalls rarely have 3+ specialist holders)." },
  { key: "midsmall", label: "Mid/Small Sleeve",
    desc: "The backtested satellite sleeve: ungated top-30 mid/small gems by gem score, binary-event veto only. +233.8% cum (Feb 2023 → Jul 2026), Sharpe 1.39 — a tail-driven lottery profile that needs full breadth and is sized, not filtered." },
];

export default function HiddenGemsPage() {
  const { token } = useAuth();
  const [dates, setDates] = useState([]);
  const [view, setView] = useState("converged");

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/stats/hidden-gems/screener/dates`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setDates(d.dates || []))
      .catch(() => {});
  }, [token]);

  const active = VIEWS.find((v) => v.key === view);

  return (
    <div className="p-6 space-y-5" data-testid="hidden-gems-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Gem className="w-6 h-6 text-primary" /> Hidden Gems
        </h1>
        <div className="flex gap-1 mt-3 border border-border rounded-md p-0.5 w-fit">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              data-testid={`gems-view-${v.key}`}
              onClick={() => setView(v.key)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                view === v.key
                  ? v.key === "midsmall"
                    ? "bg-amber-500/25 text-amber-300"
                    : "bg-indigo-500/25 text-indigo-300"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{active.desc}</p>
      </div>

      {view === "converged"
        ? <ConvergedScreener token={token} dates={dates} />
        : <MidSmallSleeve token={token} dates={dates} />}
    </div>
  );
}
